import { useCallback, useRef, useState } from 'react';

/**
 * #605 — the UI lock of a user action that sends a write (logic-reviewer §15,
 * `agent-docs/code-patterns.md § Frontend Conventions`).
 *
 * `run(fn)` returns at once while a run is busy, so a second tap sends no
 * copy. The lock is a ref, which is true at once, also before the next render,
 * and a state, which disables the element and shows a busy label. `finally`
 * ends the lock, so a refusal or a network error ends it too. `fn` gives each
 * result to the user: the effect, a toast, `showError(err)`.
 *
 *   const save = useBusy();
 *   <button disabled={save.busy} onClick={() => save.run(async () => { … })}>
 */
export function useBusy() {
  const ref = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (ref.current) return undefined;
    ref.current = true;
    setBusy(true);
    try {
      return await fn();
    } finally {
      ref.current = false;
      setBusy(false);
    }
  }, []);
  return { busy, run };
}

/** The value of `useBusy()`, for a prop that shares one lock. */
export type Busy = ReturnType<typeof useBusy>;

/** The same lock for each row: the bell of one official, the box of one registration. */
export function useBusyKeys() {
  const ref = useRef(new Set<string>());
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  const run = useCallback(async <T>(key: string, fn: () => Promise<T>): Promise<T | undefined> => {
    if (ref.current.has(key)) return undefined;
    ref.current.add(key);
    setKeys(new Set(ref.current));
    try {
      return await fn();
    } finally {
      ref.current.delete(key);
      setKeys(new Set(ref.current));
    }
  }, []);
  const isBusy = useCallback((key: string) => keys.has(key), [keys]);
  return { isBusy, run, any: keys.size > 0 };
}
